import React from 'react';
import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { ITopic } from '../../../../Interfaces/ITopic';
import { groupTopicsByReferenceType } from '../../../../services/notificationTopics.service';

interface NotificationPreferencesProps {
  topics: ITopic[] | undefined;
  showAtLogin?: boolean;
  setShowAtLogin?: (showAtLogin: boolean) => void;
  preferences: { [key: number]: boolean };
  setPreferences: (preferences: { [key: number]: boolean }) => void;
  /** Themen, die über eine Benutzergruppe aktiviert sind. Das Backend
   *  vereinigt Nutzer- und Gruppenmaske, der Nutzer kann sie also nicht für
   *  sich abschalten — der Hinweis erklärt das. */
  forcedByGroup?: number[];
  /** Schalter nach Topic.ReferenceType gruppieren (Vorgänge/Aufgaben/Weitere). */
  groupByReferenceType?: boolean;
}

const FORCED_HINT =
  'Dieses Thema ist über eine Benutzergruppe aktiviert und wird daher auch zugestellt, wenn der Schalter hier aus ist.';

export const NotificationPreferences: React.FC<
  NotificationPreferencesProps
> = ({
  topics,
  preferences,
  setPreferences,
  showAtLogin,
  setShowAtLogin,
  forcedByGroup = [],
  groupByReferenceType = false,
}) => {
  const handleToggle = (topicId: number) => {
    setPreferences({
      ...preferences,
      [topicId]: !preferences[topicId],
    });
  };

  if (!topics) {
    return (
      <Box>
        <Typography variant='h6'>Benachrichtigungseinstellungen</Typography>
        <Typography>Fehler beim Laden der Themen</Typography>
      </Box>
    );
  }

  const renderTopicRow = (topic: ITopic) => {
    const isOn = preferences[topic.idTopic] || false;
    const showForcedHint = !isOn && forcedByGroup.includes(topic.idTopic);

    return (
      <Box
        key={topic.idTopic}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        mt={2}
        gap={2}
      >
        <Box display='flex' alignItems='center' flexWrap='wrap' gap={1}>
          <Typography>{topic.Description}</Typography>
          {showForcedHint && (
            <Tooltip title={FORCED_HINT}>
              <Chip
                size='small'
                color='warning'
                variant='outlined'
                label='durch Benutzergruppe aktiviert'
              />
            </Tooltip>
          )}
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={isOn}
              onChange={() => handleToggle(topic.idTopic)}
              name={topic.Name}
              color='primary'
            />
          }
          label={isOn ? 'An' : 'Aus'}
        />
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant='h6'>Benachrichtigungseinstellungen</Typography>
      {setShowAtLogin && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          mt={2}
        >
          <Typography>Benachrichtigungen beim Login anzeigen</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showAtLogin}
                onChange={() =>
                  setShowAtLogin(
                    showAtLogin == null ? false : !Boolean(showAtLogin)
                  )
                }
                name='showAtLogin'
                color='primary'
              />
            }
            label={showAtLogin ? 'An' : 'Aus'}
          />
        </Box>
      )}

      {groupByReferenceType
        ? groupTopicsByReferenceType(topics).map((group, index) => (
            <Box key={group.label}>
              {index > 0 && <Divider sx={{ mt: 3 }} />}
              <Typography
                variant='subtitle2'
                color='text.secondary'
                sx={{ mt: 3 }}
              >
                {group.label}
              </Typography>
              {group.topics.map(renderTopicRow)}
            </Box>
          ))
        : topics.map(renderTopicRow)}
    </Box>
  );
};
